import { AnimatePresence, motion } from 'framer-motion';
import { createContext, ReactNode, useState, useRef, useEffect } from 'react';
import Button from '../Form/Button';
import Title from '../Title';
import { useTranslation } from "react-i18next";
import { faCheck, faXmark } from '@fortawesome/pro-solid-svg-icons';
import GenericModalShell from './GenericModalShell';

export const ModalContext = createContext<{
    show: (title: string, body: string) => Promise<boolean>;
    hide: () => void;
    withModal: (
        display: { title: string; body: string },
        onYes: Function,
        onNo?: Function
    ) => () => Promise<void>;
}>({
    show: async () => false,
    hide: () => void 0,
    withModal: () => async () => void 0,
});

const Modal = ({ children }: { children: ReactNode }) => {
    const [state, setState] = useState({
        title: '',
        body: ''
    });
    const [isVisible, setIsVisible] = useState(false);
    const [closeAction, setCloseAction] = useState<(value: boolean) => void>(() => void 0);
    const { t } = useTranslation();
    const yesButtonRef = useRef<HTMLButtonElement>(null);

    const show = (title: string, body: string) => {
        return new Promise<boolean>((res) => {
            setState({ title, body });
            setIsVisible(true);
            setCloseAction(() => (value: boolean) => res(value));
        });
    };

    const hide = () => {
        setIsVisible(false);
    };

    const withModal = (display: { title: string, body: string }, onYes: Function, onNo?: Function) => {
        return async () => {
            const act = await show(display.title, display.body);
            if (act) {
                onYes();
            } else if (onNo) {
                onNo();
            }
            hide();
        };
    };

    return (
        <ModalContext.Provider value={{ show, hide, withModal }}>
            <GenericModalShell
                isOpen={isVisible}
                onBackdropClick={() => hide()}
                initialFocusRef={yesButtonRef}
            >
                <Title text={state.title} />
                <div className="mt-4">{state.body}</div>
                <div className="flex flex-wrap gap-4 mt-8">
                    <Button
                        icon={faCheck}
                        nosubmit
                        title={t('cards:list:yes')}
                        action={() => closeAction(true)}
                        ref={yesButtonRef}
                    />
                    <Button
                        icon={faXmark}
                        secondary
                        nosubmit
                        title={t('cards:list:no')}
                        action={() => closeAction(false)}
                    />
                </div>
            </GenericModalShell>
            {children}
        </ModalContext.Provider>
    );
};

export default Modal;
